import React, { useEffect, useState, useMemo } from 'react'
import { toast } from 'react-toastify'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import { Typography } from '@mui/material'
import { getObject, getObjectsWithFilter } from '../../../api/apiClientCrud'
import { useAuth } from '../../../context/AuthContext'
import { useLocation, useOutletContext } from 'react-router-dom'
import Toolbar from '../../toolbar/Toolbar'
import { BiSolidBusiness } from 'react-icons/bi'
import { BsPersonFill } from 'react-icons/bs'
import { TbPhoneFilled, TbMailFilled, TbRoute2 } from 'react-icons/tb'
import { FaLocationDot } from 'react-icons/fa6'
import SubModule from '../../module/SubModule'
import MapGoogle from '../../map/Map'
import InvoiceSubModule from '../../subModules/InvoiceSubModule'
import InterventionSubModule from '../../subModules/InterventionSubModule'
import CreditSubModule from '../../subModules/CreditSubModule'
import QuoteSubModule from '../../subModules/QuoteSubModule'
import PaymentSubModule from '../../subModules/PaymentSubModule'
import EmailSubModule from '../../subModules/EmailSubModule'
import AuditSubModule from '../../subModules/AuditSubModule'
import Loading from '../../common/Loading'
import NotFound from '../../common/NotFound'

function ViewCustomer() {
  const { user } = useAuth()
  const userRight = useOutletContext()
  const location = useLocation()
  const customerId = location?.state?.id

  const [statusList, setStatusList] = useState([])
  const [customer, setCustomer] = useState(null)
  const [distance, setDistance] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!customerId) {
      setLoading(false)
      return
    }

    const loadData = async () => {
      try {
        const [statusRes, customerRes] = await Promise.all([
          getObjectsWithFilter('status'),
          getObject('customers', customerId)
        ])
        setStatusList(statusRes.data)
        setCustomer(customerRes.data)
      } catch (error) {
        toast.error('Erreur lors du chargement', { autoClose: 2000 })
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [customerId])

  const toolbar = useMemo(
    () => ({
      accessRight: userRight,
      title: () => {
        return `Client ${customer.customer_name}`
      },
      objectType: 'customers',
      actions: { edit: true, audits: true, back: true, list: true }
    }),
    [userRight, customer]
  )

  const subModules = useMemo(() => {
    if (!customer) return { tabsNames: [], tabs: [] }

    return {
      tabsNames: [
        { tabName: 'Devis', tabIndex: '1' },
        { tabName: 'Interventions', tabIndex: '2' },
        { tabName: 'Factures', tabIndex: '3' },
        { tabName: 'Avoirs', tabIndex: '4' },
        { tabName: 'Paiements', tabIndex: '5' },
        { tabName: 'Emails', tabIndex: '6' },
        { tabName: 'Audits', tabIndex: '7' }
      ],
      tabs: [
        {
          tabIndex: '1',
          component: () => (
            <QuoteSubModule location={location} statusList={statusList} user={user} userRight={userRight} />
          )
        },
        {
          tabIndex: '2',
          component: () => (
            <InterventionSubModule userRight={userRight} user={user} location={location} statusList={statusList} />
          )
        },
        {
          tabIndex: '3',
          component: () => (
            <InvoiceSubModule userRight={userRight} user={user} location={location} statusList={statusList} />
          )
        },
        {
          tabIndex: '4',
          component: () => (
            <CreditSubModule location={location} statusList={statusList} user={user} userRight={userRight} />
          )
        },
        { tabIndex: '5', component: () => <PaymentSubModule user={user} userRight={userRight} location={location} /> },
        { tabIndex: '6', component: () => <EmailSubModule userRight={userRight} customer={customer} user={user} /> },
        {
          tabIndex: '7',
          component: () => (
            <AuditSubModule userRight={userRight} user={user} location={location} objectType='customers' />
          )
        }
      ]
    }
  }, [customer, statusList, userRight, user, location])

  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          height: '100%',
          minHeight: '100px',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Loading />
      </Box>
    )
  if (!customer?.id) return <NotFound title='Client introuvable' />

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 65px)', overflowY: 'auto', flexGrow: 1 }}
    >
      <Toolbar options={toolbar} object={customer} />

      <Paper sx={{ width: '90%', marginLeft: '5%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', margin: '2%' }}>
          <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', marginRight: '5px' }}>
            <Typography sx={{ marginBottom: '5px' }}>Client</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
              {customer.customer_type === 2 ? <BsPersonFill size={30} /> : <BiSolidBusiness size={30} />}
              <Typography sx={{ marginLeft: '5px' }}>{customer.customer_name}</Typography>
            </Box>

            {customer.customer_type === 1 && (
              <>
                <Typography>{`SIRET : ${customer.siret}`}</Typography>
                <Typography>{`SIREN : ${customer.siren}`}</Typography>
              </>
            )}

            <Typography sx={{ marginTop: '5px', marginBottom: '5px' }}>Contact</Typography>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                <TbPhoneFilled size={30} />
                <Typography sx={{ marginLeft: '5px' }}>{customer.phone}</Typography>
              </Box>
              {customer.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                  <TbMailFilled size={30} />
                  <Typography sx={{ marginLeft: '5px' }}>{customer.email}</Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flex: 1, flexDirection: 'row' }}>
            <Box sx={{ flex: 1, marginRight: '5px' }}>
              <Typography>Adresse</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
                <FaLocationDot size={30} />
                <Box sx={{ marginLeft: '2px' }}>
                  <Typography>{customer.address1}</Typography>
                  {customer.address2 && <Typography>{customer.address2}</Typography>}
                  <Typography>{`${customer.zip} ${customer.city}`}</Typography>
                </Box>
              </Box>

              {distance?.distance?.text && (
                <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', marginTop: '5px' }}>
                  <TbRoute2 size={30} />
                  <Box sx={{ marginLeft: '2px' }}>
                    <Typography>{`Distance : ${distance.distance.text}`}</Typography>
                    <Typography>{`Durée : ${distance.duration.text}`}</Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <Box sx={{ width: 300, height: 300 }}>
              <MapGoogle setDistance={setDistance} customer={customer} company={user.company} />
            </Box>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ width: '90%', marginLeft: '5%', marginTop: '10px', marginBottom: '10px' }}>
        <SubModule options={subModules} />
      </Paper>
    </Box>
  )
}

export default ViewCustomer
